export interface AgenticFeaturesResponse {
  ok: boolean;
  enabled: boolean;
  robinhood?: boolean;
  mcpUrl: string;
}

export interface AgenticOAuthCooldown {
  blocked: boolean;
  startBlocked?: boolean;
  canStart: boolean;
  retryAfterSec: number;
  startsRemaining: number;
  cooldownMs: number;
  message: string | null;
}

export interface AgenticTokenExpiry {
  accessTokenExpiresAt: string | null;
  accessTokenExpiresInSec: number | null;
  hasRefreshToken: boolean;
}

export interface AgenticStatusResponse {
  ok: boolean;
  connected: boolean;
  updatedAt: string | null;
  mcpUrl: string;
  tokenExpiry?: AgenticTokenExpiry | null;
  oauthCooldown?: AgenticOAuthCooldown;
}

export interface AgenticToolDefinition {
  name: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
}

export interface AgenticRobinhoodAccount {
  accountNumber: string;
  name: string;
  nickname: string | null;
  brokerageAccountType: string | null;
  tradingType: string | null;
  isDefault: boolean;
  agenticAllowed: boolean;
  maskedAccountNumber: string;
}

export interface AgenticAccountsResponse {
  ok: boolean;
  accounts: AgenticRobinhoodAccount[];
}

export interface AgenticSnapshotToolResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

export interface AgenticSnapshotAccountData {
  portfolio?: AgenticSnapshotToolResult;
  equityPositions?: AgenticSnapshotToolResult;
  optionPositions?: AgenticSnapshotToolResult;
  equityQuotes?: AgenticSnapshotToolResult;
  optionQuotes?: AgenticSnapshotToolResult;
}

export interface AgenticSnapshot {
  version: number;
  capturedAt: string | null;
  refreshedAt: string | null;
  updatedAt: string | null;
  accounts: AgenticRobinhoodAccount[];
  byAccount: Record<string, AgenticSnapshotAccountData>;
  tools?: Record<string, AgenticSnapshotToolResult & { args?: Record<string, unknown>; savedAt?: string }>;
}

export interface AgenticSnapshotResponse {
  ok: boolean;
  hasSnapshot: boolean;
  snapshot: AgenticSnapshot | null;
  settings?: AgenticSettings;
  nextAutoRefreshSec?: number | null;
}

export interface AgenticSettings {
  snapshotAutoRefreshEnabled: boolean;
  snapshotAutoRefreshMinutes: number;
  autoImportEnabled: boolean;
  autoImportLastRunAt: string | null;
}

export type AgenticUpdateKind = "sto" | "btc" | "exp" | "asgn";
export type AgenticUpdateStatus = "pending" | "added" | "manual";

export interface AgenticUpdateItem {
  id: string;
  kind: AgenticUpdateKind;
  detectedAt: string;
  receivedAt: string | null;
  ticker: string;
  side: "put" | "call" | string;
  strike: number;
  expDate: string;
  quantity: number;
  optionSymbol: string | null;
  avgPrice: number | null;
  label: string;
  contractKey: string;
  status: AgenticUpdateStatus;
  ledgerTxnIds: number[];
  handledAt: string | null;
  importSource?: "auto" | null;
  /** True when Robinhood still shows expiration/assignment as pending settlement. */
  settlementPending?: boolean;
}

export interface AgenticUpdatesResponse {
  ok: boolean;
  updates: AgenticUpdateItem[];
  hasPreviousSnapshot: boolean;
  detectedCount: number;
  pendingCount?: number;
  historyCount?: number;
  settings?: AgenticSettings;
  autoImport?: { imported: number; skipped: number; linked: number; ran: boolean } | null;
}

export interface AgenticSettingsResponse {
  ok: boolean;
  settings: AgenticSettings;
}

export interface AgenticToolsResponse {
  ok: boolean;
  tools: AgenticToolDefinition[];
}

function jsonHeaders(): Record<string, string> {
  return { Accept: "application/json", "Content-Type": "application/json" };
}

export class AgenticOAuthCooldownError extends Error {
  retryAfterSec: number;

  constructor(message: string, retryAfterSec: number) {
    super(message);
    this.name = "AgenticOAuthCooldownError";
    this.retryAfterSec = retryAfterSec;
  }
}

function readAgenticError(payload: { detail?: { message?: string; retryAfterSec?: number; code?: string } }, status: number): Error {
  const message = payload.detail?.message ?? `Agentic request failed (${status})`;
  if (status === 429 && payload.detail?.code === "agentic_oauth_cooldown") {
    return new AgenticOAuthCooldownError(message, payload.detail.retryAfterSec ?? 0);
  }
  if (status === 429 && payload.detail?.retryAfterSec) {
    return new AgenticOAuthCooldownError(message, payload.detail.retryAfterSec);
  }
  return new Error(message);
}

export async function fetchAgenticFeatures(): Promise<AgenticFeaturesResponse> {
  const res = await fetch("/agentic/features", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`agentic/features failed (${res.status})`);
  return (await res.json()) as AgenticFeaturesResponse;
}

export async function fetchAgenticStatus(): Promise<AgenticStatusResponse> {
  const res = await fetch("/agentic/status", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticStatusResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw readAgenticError(payload, res.status);
  }
  return payload;
}

export async function startAgenticOAuth(returnPath: string): Promise<{ authorizationUrl: string | null; connected: boolean; reused?: boolean }> {
  const res = await fetch("/agentic/oauth/start", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ returnPath }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    authorizationUrl?: string | null;
    connected?: boolean;
    reused?: boolean;
    detail?: { message?: string; retryAfterSec?: number; code?: string };
  };
  if (!res.ok) {
    throw readAgenticError(payload, res.status);
  }
  return {
    authorizationUrl: payload.authorizationUrl ?? null,
    connected: Boolean(payload.connected),
  };
}

export async function completeAgenticOAuth(callbackUrl: string): Promise<AgenticStatusResponse> {
  const res = await fetch("/agentic/oauth/complete", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ callbackUrl }),
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticStatusResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw readAgenticError(payload, res.status);
  }
  return payload;
}

export async function disconnectAgenticOAuth(): Promise<void> {
  const res = await fetch("/agentic/oauth", {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { detail?: { message?: string } };
    throw new Error(payload.detail?.message ?? `agentic/oauth delete failed (${res.status})`);
  }
}

export async function fetchAgenticTools(): Promise<AgenticToolDefinition[]> {
  const res = await fetch("/agentic/tools", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticToolsResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/tools failed (${res.status})`);
  }
  return payload.tools ?? [];
}

export async function fetchAgenticAccounts(): Promise<AgenticRobinhoodAccount[]> {
  const res = await fetch("/agentic/accounts", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticAccountsResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/accounts failed (${res.status})`);
  }
  return payload.accounts ?? [];
}

export async function fetchAgenticEquityQuotes(symbols: string[]): Promise<Map<string, number>> {
  const unique = [...new Set(symbols.map((s) => s.trim().toUpperCase()).filter(Boolean))];
  if (!unique.length) return new Map();
  const res = await fetch("/agentic/equity-quotes", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ symbols: unique }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    quotes?: Record<string, number>;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/equity-quotes failed (${res.status})`);
  }
  const map = new Map<string, number>();
  for (const [symbol, price] of Object.entries(payload.quotes ?? {})) {
    if (typeof price === "number" && Number.isFinite(price)) {
      map.set(symbol.trim().toUpperCase(), price);
    }
  }
  return map;
}

/** Sector (and industry) from Robinhood get_equity_fundamentals. */
export async function fetchAgenticEquitySectors(symbols: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(symbols.map((s) => s.trim().toUpperCase()).filter(Boolean))];
  if (!unique.length) return new Map();
  const res = await fetch("/agentic/equity-fundamentals", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ symbols: unique }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    sectors?: Record<string, string>;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/equity-fundamentals failed (${res.status})`);
  }
  const map = new Map<string, string>();
  for (const [symbol, sector] of Object.entries(payload.sectors ?? {})) {
    if (typeof sector === "string" && sector.trim()) {
      map.set(symbol.trim().toUpperCase(), sector.trim());
    }
  }
  return map;
}

export interface AgenticOptionQuote {
  mid: number;
  popShort: number | null;
  popLong: number | null;
}

export async function fetchAgenticOptionQuotes(
  instrumentIds: string[],
): Promise<Map<string, AgenticOptionQuote>> {
  const unique = [...new Set(instrumentIds.map((id) => id.trim()).filter(Boolean))];
  if (!unique.length) return new Map();
  const res = await fetch("/agentic/option-quotes", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ instrument_ids: unique }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    quotes?: Record<string, AgenticOptionQuote>;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/option-quotes failed (${res.status})`);
  }
  const map = new Map<string, AgenticOptionQuote>();
  for (const [instrumentId, quote] of Object.entries(payload.quotes ?? {})) {
    if (!quote || typeof quote !== "object") continue;
    const mid = quote.mid;
    if (typeof mid !== "number" || !Number.isFinite(mid)) continue;
    map.set(instrumentId.trim(), {
      mid,
      popShort:
        typeof quote.popShort === "number" && Number.isFinite(quote.popShort) ? quote.popShort : null,
      popLong:
        typeof quote.popLong === "number" && Number.isFinite(quote.popLong) ? quote.popLong : null,
    });
  }
  return map;
}

export async function fetchAgenticSnapshot(): Promise<AgenticSnapshotResponse> {
  const res = await fetch("/agentic/snapshot", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    cache: "no-store",
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticSnapshotResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/snapshot failed (${res.status})`);
  }
  return payload;
}

export async function refreshAgenticSnapshot(): Promise<AgenticSnapshotResponse> {
  const res = await fetch("/agentic/snapshot/refresh", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: "{}",
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticSnapshotResponse & {
    detail?: { message?: string; retryAfterSec?: number; code?: string };
  };
  if (!res.ok) {
    throw readAgenticError(payload, res.status);
  }
  return payload;
}

export async function fetchAgenticSettings(): Promise<AgenticSettings> {
  const res = await fetch("/agentic/settings", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticSettingsResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/settings failed (${res.status})`);
  }
  return payload.settings;
}

export async function updateAgenticSettings(
  settings: Partial<AgenticSettings>,
): Promise<AgenticSettings> {
  const res = await fetch("/agentic/settings", {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(settings),
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticSettingsResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/settings update failed (${res.status})`);
  }
  return payload.settings;
}

export async function callAgenticTool(
  name: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const res = await fetch("/agentic/tools/call", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ name, arguments: args }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    result?: unknown;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/tools/call failed (${res.status})`);
  }
  return payload.result;
}

export async function fetchAgenticUpdates(): Promise<AgenticUpdatesResponse> {
  const res = await fetch("/agentic/updates", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as AgenticUpdatesResponse & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/updates failed (${res.status})`);
  }
  return payload;
}

export async function patchAgenticUpdate(
  updateId: string,
  body: {
    status: AgenticUpdateStatus;
    ledgerTxnIds?: number[];
    update?: AgenticUpdateItem;
  },
): Promise<void> {
  const res = await fetch(`/agentic/updates/${encodeURIComponent(updateId)}`, {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  const payload = (await res.json().catch(() => ({}))) as { detail?: { message?: string } };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `agentic/updates patch failed (${res.status})`);
  }
}
